import { useState } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import type { Id } from '../../../../convex/_generated/dataModel';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

interface PendingReference {
  _id: Id<"references">;
  title: string;
  description?: string;
  type: "screenshot" | "component" | "flow" | "screencast";
  userId: Id<"users">;
  ownerEmail: string | null;
  ownerName: string | null;
  moderationStatus: "pending";
  isPublic: boolean;
  _creationTime: number;
  publicMetadata?: {
    publishedAt: number;
    views: number;
    likes: number;
    featured: boolean;
  };
}

export function ModerationPage() {
  const navigate = useNavigate();
  const [rejectReason, setRejectReason] = useState<string>("");
  const [rejectingId, setRejectingId] = useState<Id<"references"> | null>(null);

  // Check if user is admin
  const isAdmin = useQuery(api.publicDirectory.isAdmin);
  const pendingReferences = useQuery(api.publicDirectory.getPendingReferences);
  const approveReference = useMutation(api.publicDirectory.approveReference);
  const rejectReference = useMutation(api.publicDirectory.rejectReference);

  // Show loading state
  if (isAdmin === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="loading loading-spinner loading-lg"></div>
          <p className="mt-4 text-gray-600">Checking permissions...</p>
        </div>
      </div>
    );
  }

  // Show error if not admin
  if (isAdmin === false) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="card bg-white shadow-comic border-2 border-gray-800 max-w-md">
          <div className="card-body text-center">
            <h2 className="card-title text-2xl text-gray-800 justify-center">Access Denied</h2>
            <p className="text-gray-600 mt-2">You don't have permission to access this page.</p>
            <div className="card-actions justify-center mt-4">
              <button 
                className="btn btn-primary"
                onClick={() => navigate('/')}
              >
                Go Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleApprove = async (referenceId: Id<"references">) => {
    try {
      await approveReference({ referenceId });
      toast.success('Reference approved successfully');
    } catch (error) {
      toast.error(`Failed to approve reference: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleReject = async (referenceId: Id<"references">) => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    try {
      await rejectReference({ 
        referenceId, 
        reason: rejectReason 
      });
      toast.success('Reference rejected');
      setRejectReason("");
      setRejectingId(null);
    } catch (error) {
      toast.error(`Failed to reject reference: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  const getTypeBadgeColor = (type: string) => {
    switch (type) {
      case 'screenshot':
        return 'bg-blue-100 text-blue-800';
      case 'component':
        return 'bg-green-100 text-green-800';
      case 'flow':
        return 'bg-purple-100 text-purple-800';
      case 'screencast':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Moderation Queue</h1>
          <p className="text-gray-600">Review and approve pending references for public display</p>
        </div>

        {/* Pending References List */}
        {pendingReferences === undefined ? (
          <div className="text-center py-12">
            <div className="loading loading-spinner loading-lg"></div>
            <p className="mt-4 text-gray-600">Loading pending references...</p>
          </div>
        ) : pendingReferences.length === 0 ? (
          <div className="card bg-white shadow-comic border-2 border-gray-800">
            <div className="card-body text-center py-12">
              <p className="text-xl text-gray-600">No pending references to review</p>
              <p className="text-gray-500 mt-2">All references have been processed</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {pendingReferences.map((ref: PendingReference) => (
              <div 
                key={ref._id} 
                className="card bg-white shadow-comic border-2 border-gray-800"
              >
                <div className="card-body">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    {/* Reference Info */}
                    <div className="flex-1">
                      <div className="flex items-start gap-3 mb-2">
                        <h3 className="text-xl font-bold text-gray-800 flex-1">
                          {ref.title}
                        </h3>
                        <span className={`badge badge-lg ${getTypeBadgeColor(ref.type)}`}>
                          {ref.type}
                        </span>
                      </div>
                      
                      {ref.description && (
                        <p className="text-gray-600 mb-3 line-clamp-2">
                          {ref.description}
                        </p>
                      )}

                      <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                        <div>
                          <span className="font-semibold">Owner:</span>{' '}
                          {ref.ownerName || ref.ownerEmail || 'Unknown'}
                        </div>
                        <div>
                          <span className="font-semibold">Submitted:</span>{' '}
                          {formatDate(ref._creationTime)}
                        </div>
                        {ref.publicMetadata?.publishedAt && (
                          <div>
                            <span className="font-semibold">Published:</span>{' '}
                            {formatDate(ref.publicMetadata.publishedAt)}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row gap-2 lg:flex-col">
                      <button
                        className="btn btn-success btn-sm"
                        onClick={() => handleApprove(ref._id)}
                      >
                        ✓ Approve
                      </button>
                      
                      {rejectingId === ref._id ? (
                        <div className="flex flex-col gap-2">
                          <textarea
                            className="textarea textarea-bordered textarea-sm"
                            placeholder="Reason for rejection..."
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            rows={2}
                          />
                          <div className="flex gap-2">
                            <button
                              className="btn btn-error btn-sm flex-1"
                              onClick={() => handleReject(ref._id)}
                            >
                              Confirm Reject
                            </button>
                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={() => {
                                setRejectingId(null);
                                setRejectReason("");
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          className="btn btn-error btn-sm"
                          onClick={() => {
                            setRejectingId(ref._id);
                            setRejectReason("");
                          }}
                        >
                          ✗ Reject
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Summary */}
        {pendingReferences && pendingReferences.length > 0 && (
          <div className="mt-8 card bg-yellow-50 border-2 border-yellow-300">
            <div className="card-body">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">{pendingReferences.length}</span> reference{pendingReferences.length !== 1 ? 's' : ''} pending review
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


